import { useState } from "react";
import { Menu, Swords, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { streamCompare } from "@/lib/api";
import { PROVIDER_META, findModel, modelsForKind } from "@/lib/meta";
import { CATEGORY_ICONS } from "@/lib/meta";
import { Markdown } from "@/components/MessageBubble";
import { ThinkingToggle } from "@/components/ThinkingToggle";

const ModelSelect = ({ catalog, category, value, onChange, slot }) => {
  const recs = new Set((category?.recommendations || []).map((r) => `${r.provider}/${r.model}`));
  return (
    <select data-testid={`compare-model-select-${slot}`} value={`${value.provider}/${value.model}`} onChange={(e) => { const [provider, model] = e.target.value.split("/"); onChange({ provider, model }); }}
      className="w-full bg-[#0D0F18] border border-slate-800 focus:border-indigo-500 outline-none rounded-xl px-3 py-2.5 text-sm text-slate-100">
      {catalog.providers.map((p) => (
        <optgroup key={p.id} label={p.name}>
          {modelsForKind(p, "text").map((m) => (
            <option key={m.id} value={`${p.id}/${m.id}`}>{m.name}{recs.has(`${p.id}/${m.id}`) ? " ★" : ""}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
};

export default function ComparePage({ catalog, onStart, onOpenSidebar }) {
  const textCategories = (catalog?.categories || []).filter((c) => c.kind !== "image");
  const [categoryId, setCategoryId] = useState("general_chat");
  const category = textCategories.find((c) => c.id === categoryId) || textCategories[0];
  const defaults = (cat) => [
    { provider: cat.recommendations[0].provider, model: cat.recommendations[0].model },
    { provider: cat.recommendations[1].provider, model: cat.recommendations[1].model },
  ];
  const [models, setModels] = useState(() => (category ? defaults(category) : null));
  const [prompt, setPrompt] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!catalog || !category) return null;
  const slots = models || defaults(category);

  const pickCategory = (id) => {
    setCategoryId(id);
    setModels(defaults(textCategories.find((c) => c.id === id)));
    setResults(null);
  };

  const run = async () => {
    const text = prompt.trim();
    if (!text || busy) return;
    if (slots[0].provider === slots[1].provider && slots[0].model === slots[1].model) return toast.error("Pick two different models");
    setBusy(true);
    setResults([{ text: "", status: "streaming" }, { text: "", status: "streaming" }]);
    const upd = (slot, patch) => setResults((r) => r.map((x, i) => (i === slot ? { ...x, ...patch } : x)));
    try {
      await streamCompare({ category: category.id, prompt: text, models: slots }, {
        delta: (e) => setResults((r) => r.map((x, i) => (i === e.slot ? { ...x, text: x.text + e.content } : x))),
        done: (e) => upd(e.slot, { status: "done", elapsed: e.elapsed }),
        error: (e) => { upd(e.slot, { status: "error", error: e.detail }); toast.error(e.detail || "Model error"); },
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const continueWith = (slot) => {
    const m = slots[slot];
    onStart({ category: category.id, provider: m.provider, model: m.model,
      seed_messages: [{ role: "user", content: prompt.trim() }, { role: "assistant", content: results[slot].text }] });
  };

  return (
    <div className="flex-1 overflow-y-auto relative" data-testid="compare-view">
      <div className="absolute inset-x-0 top-0 h-[360px] grid-bg pointer-events-none" />
      <header className="sticky top-0 z-10 h-16 flex items-center px-4 lg:px-10 bg-[#090A0F]/70 backdrop-blur-xl border-b border-slate-800/60">
        <button onClick={onOpenSidebar} className="lg:hidden mr-3 text-slate-400 hover:text-white" data-testid="open-sidebar-btn"><Menu className="w-5 h-5" /></button>
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400">Compare mode</p>
      </header>

      <div className="relative max-w-6xl mx-auto px-4 lg:px-10 py-10">
        <div className="rise">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-100">Two models. One prompt.<br className="hidden sm:block" /> See who wins.</h1>
          <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-xl leading-relaxed">Pick an inquiry type, choose two models, and watch both answers stream side by side. Continue the conversation with the one you prefer.</p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="compare-category-list">
          {textCategories.map((c) => {
            const Icon = CATEGORY_ICONS[c.icon] || CATEGORY_ICONS.MessageSquare;
            const active = c.id === category.id;
            return (
              <button key={c.id} onClick={() => pickCategory(c.id)} data-testid={`compare-category-${c.id}`}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm transition-colors ${active ? "bg-indigo-500 border-indigo-500 text-white" : "bg-[#11131E] border-slate-800 text-slate-300 hover:border-indigo-500/40 hover:text-white"}`}>
                <Icon className="w-4 h-4" /> {c.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {slots.map((m, i) => (
            <div key={i}>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Model {i === 0 ? "A" : "B"}</p>
              <div className="flex items-center gap-2">
                <div className="flex-1"><ModelSelect catalog={catalog} category={category} value={m} slot={i} onChange={(v) => setModels(slots.map((x, j) => (j === i ? { ...v, thinking: false } : x)))} /></div>
                <ThinkingToggle provider={m.provider} model={m.model} enabled={!!m.thinking} size="sm" onChange={(t) => setModels(slots.map((x, j) => (j === i ? { ...x, thinking: t } : x)))} />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <textarea data-testid="compare-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="Ask anything — both models get exactly this prompt"
            className="flex-1 bg-[#11131E] border border-slate-800 focus:border-indigo-500/60 outline-none rounded-2xl px-4 py-3 text-[15px] text-slate-100 placeholder:text-slate-600 resize-none" />
          <button onClick={run} disabled={busy || !prompt.trim()} data-testid="compare-run-btn" className="shrink-0 inline-flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white font-medium px-6 py-3 rounded-2xl transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Swords className="w-4 h-4" />} Compare
          </button>
        </div>

        {results && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="compare-results">
            {results.map((r, i) => {
              const meta = PROVIDER_META[slots[i].provider];
              const Icon = meta.icon;
              const model = findModel(catalog, slots[i].provider, slots[i].model);
              return (
                <div key={i} className="rise flex flex-col rounded-2xl bg-[#141726] border border-slate-800 shadow-lg min-w-0" data-testid={`compare-result-${i}`}>
                  <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800/80">
                    <span className={`inline-flex items-center gap-2 text-sm font-medium ${meta.color}`}><Icon className="w-4 h-4" /> {model?.name}</span>
                    <span className="text-[11px] text-slate-500" data-testid={`compare-status-${i}`}>
                      {r.status === "streaming" ? "streaming…" : r.status === "error" ? "failed" : `${r.elapsed}s`}
                    </span>
                  </div>
                  <div className="flex-1 px-5 py-4 text-slate-200 min-h-[120px]">
                    {r.text ? <Markdown>{r.text}</Markdown> : r.status === "error" ? <p className="text-sm text-rose-400">{r.error}</p> : (
                      <div className="flex gap-1.5 pt-2"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /></div>
                    )}
                  </div>
                  {r.status === "done" && (
                    <div className="px-5 py-3 border-t border-slate-800/80">
                      <button onClick={() => continueWith(i)} data-testid={`compare-continue-${i}`} className="inline-flex items-center gap-2 text-sm text-indigo-300 hover:text-white transition-colors">
                        Continue with {model?.name} <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
